import { ChangeEvent, FC, useEffect, useState } from "react";
import styles from "./SettingsProfile.module.scss";
import { Button, CircularProgress, TextField } from "@mui/material";
import { LoginMailIcon, UserIcon } from "@features/common/SvgIcons";
import {
  useUpdateProfileMutation,
  useLazyFetchProfileQuery,
} from "../settingsAPI";
import { useAppDispatch, useAppSelector } from "@redux/hooks";
import {
  EnabledNavigation,
  notifyUser,
  setProfileData,
} from "@redux/slices/CommonSlice";

const SettingsProfile: FC = () => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
  });

  const [error, setError] = useState<{ [key: string]: string }>({});
  const [initialData, setInitialData] = useState(formData);

  const profile = useAppSelector((state) => state.common.profileData);

  const [updateProfile, { isLoading: isUpdateProfileLoading }] =
    useUpdateProfileMutation();

  const dispatch = useAppDispatch();

  const [triggerFetchProfile, { isLoading: isProfileLoading }] =
    useLazyFetchProfileQuery();

  useEffect(() => {
    if (profile) {
      const { first_name: firstName, last_name: lastName, email } = profile;

      const data = {
        firstName,
        lastName,
        email: email,
      };

      setFormData(data);
      setInitialData(data);
    }
  }, [profile]);

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setError((prev) => {
      const newError = { ...prev };
      delete newError[name];
      return newError;
    });
  };

  const handleValidate = () => {
    const errorData: { [key: string]: string } = {};
    const { firstName, lastName } = formData;

    if (!firstName) {
      errorData["firstName"] = "First name is required";
    }

    if (!lastName) {
      errorData["lastName"] = "Last name is required";
    }
    setError(errorData);
    return Object.keys(errorData).length === 0;
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (
      !handleValidate() ||
      isButtonDisabled ||
      isUpdateProfileLoading ||
      isProfileLoading
    )
      return;

    try {
      const { firstName, lastName, email } = formData;
      let body = {
        first_name: firstName,
        last_name: lastName,
        email,
      };

      const response = await updateProfile(body).unwrap();

      const profileResponse = await triggerFetchProfile().unwrap();
      const updatedData = profileResponse?.data;
      if (updatedData) {
        dispatch(
          setProfileData({
            corporate_name: updatedData.corporate_name || "",
            email: updatedData.email || "",
            full_name: updatedData.full_name || "",
            first_name: updatedData.first_name || "",
            last_name: updatedData.last_name || "",
            enabled_navigation:
              updatedData.enabled_navigation as EnabledNavigation,
          }),
        );
      }

      setInitialData(formData);

      dispatch(
        notifyUser({
          title: "Profile Updated",
          message: response?.message,
          severity: "success",
        }),
      );
    } catch (error: any) {
      dispatch(
        notifyUser({
          title: "Error",
          message: error?.data?.message || "Something went wrong",
          severity: "error",
        }),
      );
    }
  };

  const isDirty =
    formData.firstName !== initialData.firstName ||
    formData.lastName !== initialData.lastName;

  const isButtonDisabled =
    !isDirty ||
    !formData?.firstName ||
    !formData?.lastName ||
    !!error?.firstName ||
    !!error?.lastName;

  return (
    <div className={styles["profile"]}>
      <form onSubmit={handleSubmit}>
        <div className={styles["profile__input-group"]}>
          <div className={styles["input-continer"]}>
            <div
              className={`${styles["input-field"]} ${
                error["firstName"] && styles["input-field--error"]
              }`}
            >
              <div className={`settings-input ${styles["input-field__input"]}`}>
                <UserIcon />
                <TextField
                  variant="standard"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  placeholder="First Name"
                />
              </div>
            </div>
            {error?.firstName && (
              <div className={styles["error-message"]}>{error?.firstName}</div>
            )}
          </div>
          <div className={styles["input-continer"]}>
            <div
              className={`${styles["input-field"]} ${
                error?.lastName && styles["input-field--error"]
              }`}
            >
              <div
                className={`lastname-settings-input ${styles["input-field__input"]}`}
              >
                <TextField
                  variant="standard"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  placeholder="Last Name"
                />
              </div>
            </div>
            {error?.lastName && (
              <div className={styles["error-message"]}>{error?.lastName}</div>
            )}
          </div>
        </div>

        <div className={styles["email-input"]}>
          <div
            className={`email-settings-input ${styles["email-input__input"]}`}
          >
            <LoginMailIcon />
            <TextField
              variant="standard"
              value={formData.email}
              placeholder="Email"
              disabled
            />
          </div>
        </div>

        <div
          className={`settings-submit-button ${
            isUpdateProfileLoading
              ? styles["profile__button-loader"]
              : styles["profile__save-button"]
          }  ${
            isButtonDisabled ? styles["profile__save-button--disabled"] : ""
          } `}
        >
          {isUpdateProfileLoading ? (
            <CircularProgress
              sx={{
                color: "#C028B9",
              }}
            />
          ) : (
            <Button type="submit" disabled={isButtonDisabled}>
              Save
            </Button>
          )}
        </div>
      </form>
    </div>
  );
};

export default SettingsProfile;
